import _ from "lodash";

import initialState from "./initialState";
import types from "../actions/actionTypes";
import Constants from "../../common/constants";
import Tools from "../../helpers/tools";
import ConnectedServices from "../../helpers/vehicle/connectedServices";
import { formatVehicleUioData, formatPrivacyData, formatConnectedData } from "./vehicleReducer";
import { getLastModified, hasDealerRole } from "../../helpers/customers/dealerHelper";

export default function customerReducer(state, action) {
    switch (action.type) {
        case types.CUSTOMERS.SET:
            return setCustomerData(action.customers);
        case types.CUSTOMERS.SET_VEHICLES:
            return setCustomerVehicles(state, action.data);
        case types.CUSTOMERS.SET_SUBSCRIPTIONS:
            return setCustomerSubscriptions(state, action.data);
        case types.CUSTOMERS.SET_PREFERRED_DEALER:
            return setCustomersPreferredDealer(state, action.data);
        case types.CUSTOMERS.SET_ORDERS:
            return setCustomerOrders(state, action.data);
        case types.CUSTOMERS.SET_ORDER_DETAILS:
            return setCustomerOrderDetails(state, action.data);
        case types.CUSTOMERS.SORT:
            return sortCustomers(state, action.sortBy);
        case types.CUSTOMERS.CHANGE_CULTURE:
            return setNewCulture(state, action.data);
        case types.CUSTOMERS.SET_SHOW_VOT_ORDER_ID:
            return setShowVOTOrderId(state, action.data);
        case types.CUSTOMERS.SET_GROUPS:
            return setCustomersGroups(state, action.data);
        default:
            return state || initialState.loadInitials().customers;
    }
}

function setCustomerData(customers) {
    return [...customers.map(reduceCustomer)];
}

function reduceCustomer(customer) {
    const result = _.pick(customer, [
        `uuid`,
        `hasAcceptedTerms`,
        `hasVerifiedEmail`,
        `title`,
        `firstName`,
        `lastName`,
        `email`
    ]);

    result.toyotaID = _.get(customer, `username.device`);
    result.userName = _.get(customer, `username.portal`);

    result.addresses = _.get(customer, `contact.addresses`, []);
    result.phones = _.get(customer, `contact.phones`, []);
    result.brand = _.get(customer, `audit.initiatedBrand`, `TOYOTA`);
    result.dealerRoleModifiedBy = hasDealerRole(customer) ? getLastModified(_.get(customer, `audit.group`)) : `-`;

    reduceCulture(customer, result);
    reduceAuditDates(customer, result);

    return result;
}

function reduceCulture(customer, result) {
    const culture = _.get(customer, `culture`);

    result.locale = culture;
    result.language = culture && culture.substr(0, 2);
    result.country = culture && culture.substr(3, 2);
}

function reduceAuditDates(customer, result) {
    const createdOn = _.get(customer, `audit.createdOn`);
    const entitledOn = _.get(customer, `audit.entitledOn`);
    const acceptedTermsOn = _.get(customer, `audit.acceptedTermsOn`);
    const lastLogin = _.get(customer, `audit.lastLogin.portal`);
    const hasDealerRoleActive = hasDealerRole(customer);
    const dealerRoleModifiedOn = hasDealerRoleActive
        ? Tools.formatDate(getLastModified(_.get(customer, `audit.group`), true))
        : `-`;

    result.createdOn = Tools.formatDate(createdOn);
    result.entitledOn = Tools.formatDate(entitledOn);
    result.acceptedTermsOn = Tools.formatDate(acceptedTermsOn);
    result.lastLogin = Tools.formatDate(lastLogin);
    result.dealerRoleModifiedOn = dealerRoleModifiedOn;
}

function setCustomerVehicles(state, data) {
    return [
        ...state.map((customerElement) => {
            if (customerElement.uuid !== data.uuid) {
                return customerElement;
            } else {
                const newCustomer = Object.assign({}, customerElement);
                newCustomer.vehicles = reduceVehicles(data.vehicles, customerElement.uuid);
                newCustomer.hasVehiclesLoaded = true;
                return newCustomer;
            }
        })
    ];
}

function reduceVehicles(vehicles, uuid) {
    return vehicles.map((vehicle) => {
        const result = _.pick(vehicle.cp, [`id`, `vin`, `brand`, `device`]);
        const userData = vehicle.cp.users.find((vehicleUser) => vehicleUser.uuid === uuid);
        const userVehicleData = _.pick(userData, [
            `alias`,
            `isOwner`,
            `claimedBy`,
            `createdBy`,
            `isEntitled`,
            `entitledBy`
        ]);

        result.claimedOn = Tools.formatDate(userData.claimedOn);
        result.createdOn = Tools.formatDate(userData.createdOn);
        result.modifiedOn = Tools.formatDate(userData.modifiedOn);
        result.entitledOn = Tools.formatDate(userData.entitledOn);

        const devicesWithoutRepairStateAndConnectedEligible = vehicle.connected.filter(
            (device) =>
                _.get(device, `state`) !== `REPAIR` &&
                _.includes(window.cpAdmin.connectedSupportedDevices, _.get(device, `provider`))
        );
        const device =
            _.some(devicesWithoutRepairStateAndConnectedEligible) && devicesWithoutRepairStateAndConnectedEligible[0];
        result.connectedServiceStatus = ConnectedServices.getStatus(device, uuid);

        const vehicleUio = formatVehicleUioData(vehicle.uio);
        const privacy = formatPrivacyData(vehicle.cms);
        const vehicleConnected = formatConnectedData(vehicle.connected);
        delete vehicle.cms;

        return { ...result, ...vehicleUio, ...userVehicleData, privacy, ...vehicleConnected };
    });
}

function setCustomerSubscriptions(state, data) {
    return [
        ...state.map((customerElement) => {
            if (customerElement.uuid !== data.uuid) {
                return customerElement;
            } else {
                const newCustomer = _.cloneDeep(customerElement);
                const vehicle = newCustomer.vehicles.find((vehicle) => {
                    return vehicle.vin === data.vin;
                });
                vehicle.subscriptions = data.subscriptions ? setSubscriptionDetails(vehicle, data.subscriptions) : [];
                vehicle.hasSubscriptionsLoaded = true;
                return newCustomer;
            }
        })
    ];
}

function setCustomerOrders(state, data) {
    return [
        ...state.map((customerElement) => {
            if (customerElement.uuid !== data.uuid) {
                return customerElement;
            } else {
                const customer = Object.assign({}, customerElement);
                if (data.orders) {
                    customer.orders = data.orders;
                    customer.hasOrdersLoaded = true;
                } else {
                    customer.hasOrdersFailed = true;
                }
                return customer;
            }
        })
    ];
}

function setCustomerOrderDetails(state, data) {
    return [
        ...state.map((customerElement) => {
            if (customerElement.uuid !== data.uuid) {
                return customerElement;
            } else {
                const customer = Object.assign({}, customerElement);
                const orders = customer.orders.map((value) => Object.assign({}, value));
                const order = orders.find((order) => order.id === data.orderId);
                if (data.orderDetails) {
                    order.details = data.orderDetails;
                    if (!order.details.currentStatus) {
                        order.details.currentStatus = { currentStatus: `nostatus` };
                    }
                    order.hasOrderDetailsLoaded = true;
                } else {
                    order.hasOrderDetailsFailed = true;
                }
                customer.orders = orders;
                return customer;
            }
        })
    ];
}

function setSubscriptionDetails(vehicle, subscriptions) {
    const reducedSubscriptions = reduceSubscriptions(subscriptions);
    const subscriptionArray = (vehicle.subscriptions || []).concat(reducedSubscriptions);
    return _.uniqBy(subscriptionArray, `productId`);
}

function sortCustomers(state, sortBy) {
    const customerCopy = [...state];
    let arraySorted = customerCopy.sort((customer, nextCustomer) => {
        if (!customer[sortBy.field] && !nextCustomer[sortBy.field]) {
            return 0;
        }
        if (!customer[sortBy.field]) {
            return -1;
        }
        if (!nextCustomer[sortBy.field]) {
            return 1;
        }

        return customer[sortBy.field].localeCompare(nextCustomer[sortBy.field]);
    });

    return sortBy.order === `asc` ? arraySorted : arraySorted.reverse();
}

function reduceSubscriptions(subscriptions) {
    return [
        ...subscriptions.map((subscription) =>
            _.pick(subscription, [
                `productId`,
                `active`,
                `productName`,
                `subscriptionState`,
                `brand`,
                `isLifeSubscription`,
                `updateRequired`,
                `toU`,
                `startDate`,
                `endDate`,
                `dateCreated`,
                `dateModified`,
                `dateCancelled`
            ])
        )
    ];
}

function setNewCulture(state, data) {
    return [
        ...state.map((customerElement) => {
            if (customerElement.uuid !== data.uuid) {
                return customerElement;
            } else {
                const newCustomer = _.cloneDeep(customerElement);
                const newCulture = data.newCulture;
                newCustomer.locale = newCulture;
                newCustomer.language = newCulture && newCulture.substr(0, 2);
                newCustomer.country = newCulture && newCulture.substr(3, 2);
                return newCustomer;
            }
        })
    ];
}

function setShowVOTOrderId(state, data) {
    return [
        ...state.map((customerElement) => {
            if (customerElement.uuid !== data.uuid) {
                return customerElement;
            } else {
                const newCustomer = _.cloneDeep(customerElement);
                if (data.brand === Constants.BRANDS.TOYOTA) {
                    newCustomer.showVOTOrderId =
                        _.get(data, `content.settings.isOrderIdVehicleOrderTrackerDisplayed`) === `true`;
                } else if (data.brand === Constants.BRANDS.LEXUS) {
                    newCustomer.showVOTOrderId = _.get(data, `content.isVotOrderIdDisplayed`) === `true`;
                }
                newCustomer.votOrderIdLoaded = true;
                return newCustomer;
            }
        })
    ];
}

function setCustomersPreferredDealer(state, data) {
    return [
        ...state.map((customerElement) => {
            if (customerElement.uuid !== data.uuid) {
                return customerElement;
            } else {
                const newCustomer = _.cloneDeep(customerElement);
                if (data.preferredDealer) {
                    newCustomer.preferredDealerId = _.get(data, `preferredDealer.id`);
                    newCustomer.preferredDealerName = _.get(data, `preferredDealer.name`);
                    newCustomer.preferredDealerAddress = _.get(data, `preferredDealer.address`);
                    newCustomer.hasPreferredDealerLoaded = true;
                } else {
                    newCustomer.hasPreferredDealerFailed = true;
                }
                return newCustomer;
            }
        })
    ];
}

function setCustomersGroups(state, data) {
    return [
        ...state.map((customerElement) => {
            if (customerElement.uuid !== data.uuid) {
                return customerElement;
            } else {
                const newCustomer = _.cloneDeep(customerElement);
                newCustomer.groups = data.groups;
                return newCustomer;
            }
        })
    ];
}
